import React, { ForwardedRef } from "react";

import InfoTooltip from "../InfoTooltip";
import { TextFieldProps } from "./TextField.types";
import { useStyles } from "./TextField.styles";
import TextFieldBase from "../TextFieldBase";
import useDebounce from "../hooks/useDebounce";

const TextField = React.forwardRef(function TextField(
  props: TextFieldProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const {
    endIcon,
    info,
    infoIcon,
    InputProps: InputPropsProp,
    onChange,
    onChangeValue,
    startIcon,
    toolTipModifiers,
    ...other
  } = props;

  const classes = useStyles();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event);
    }
    if (onChangeValue) {
      onChangeValue(event.target.value);
    }
  };

  const tooltip = React.useMemo(() => {
    if (info) {
      return (
        <InfoTooltip
          title={info}
          icon={infoIcon}
          modifiers={toolTipModifiers}
        />
      );
    }
    return null;
  }, [info, infoIcon, toolTipModifiers]);

  const InputProps = React.useMemo(() => {
    let startAdornment = undefined;
    if (startIcon) {
      const icon = startIcon as React.ReactElement;
      const className = icon.props?.className || classes.startIcon;
      startAdornment = React.cloneElement(icon, {
        className: className,
      });
    }

    let endAdornment = undefined;
    if (endIcon || tooltip) {
      let endIconStyled = null;
      if (endIcon) {
        const icon = endIcon as React.ReactElement;
        const className = icon.props?.className || classes.endIcon;
        endIconStyled = React.cloneElement(icon, {
          className,
        });
      }

      endAdornment = (
        <>
          {endIconStyled && endIconStyled}
          {tooltip && tooltip}
        </>
      );
    }

    return {
      ...InputPropsProp,
      startAdornment,
      endAdornment,
    };
  }, [startIcon, classes, endIcon, InputPropsProp, tooltip]);

  return (
    <TextFieldBase
      {...other}
      InputProps={InputProps}
      ref={ref}
      onChange={handleChange}
      margin={undefined}
    />
  );
});

export default TextField;
